/**
 * Custom hook for formatting functions in UsageStats
 *
 * Extracted from UsageStats.tsx
 */

import { useCallback } from 'react';
import type { UseUsageStatsFormattingReturn } from '../types/stats.types';

/**
 * Provides formatting functions for usage stats
 */
export function useUsageStatsFormatting(): UseUsageStatsFormattingReturn {
  const formatDuration = useCallback((milliseconds: number): string => {
    if (!Number.isFinite(milliseconds)) {
      return '-';
    }

    if (milliseconds >= 1000) {
      const seconds = milliseconds / 1000;
      return `${seconds >= 10 ? seconds.toFixed(1) : seconds.toFixed(2)}s`;
    }

    return `${Math.round(milliseconds)}ms`;
  }, []);

  const formatTime = useCallback((seconds: number): string => {
    if (!Number.isFinite(seconds)) {
      return '-';
    }

    if (seconds < 60) {
      const rounded = Math.round(seconds * 10) / 10;
      const display = Number.isInteger(rounded) ? `${rounded}` : rounded.toFixed(1);
      return `${display}s`;
    }

    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}m ${secs}s`;
  }, []);

  const formatNumber = useCallback((num: number | undefined | null): string => {
    if (num === undefined || num === null || isNaN(num)) {
      return '0';
    }
    return num.toLocaleString('es-CL');
  }, []);

  return {
    formatDuration,
    formatTime,
    formatNumber,
  };
}
